import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';

import { checkValidResponse, logError } from '@cigna/chcp/shared/util';
import {
  ProviderDetail,
  ProviderByGroup,
  ProviderByTin,
  ProviderNetworkVerificationRequest,
  PathwellNetworkVerificationRequest,
  FnsNetworkVerificationRequest,
} from './provider.model';
import { LOB } from '@cigna/chcp/auth/util';

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  public providerApi: string;
  public profileApi: string;

  public CONSUMER_CODE = '1000';

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
  ) {
    this.providerApi = `${secureApi}/provider/v1`;
    this.profileApi = `${secureApi}/profile/v2`;
  }

  getProvidersByTin(tin: string, lob: LOB, contractedFlag = false) {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );
    params = params.append(lob, tin);
    if (contractedFlag) {
      params = params.append('contracted', 'true');
    }
    return this.http.get(`${this.profileApi}/tin/provider`, { params }).pipe(
      map(checkValidResponse),
      map((res): ProviderByTin[] => res.providerDetails.providerDetails),
      catchError(logError),
    );
  }

  getProvidersByGroup(providerIds: string[], contracted = false) {
    let params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('cpfIds', providerIds.join(','));

    if (contracted) {
      params = params.append('contractedOnly', 'C');
    }

    return this.http
      .get(`${this.providerApi}/providers/group`, { params })
      .pipe(
        map(checkValidResponse),
        map((res): ProviderByGroup[] => res.providerList.providers),
        catchError(logError),
      );
  }

  getProviderDetails(id: string, tin: string) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );
    return this.http
      .get(`${this.providerApi}/provider/${id}/tin/${tin}`, { params })
      .pipe(
        map(checkValidResponse),
        map((data): ProviderDetail => data.provider),
        catchError(logError),
      );
  }

  getProviderNetworkVerification(
    requestParams: ProviderNetworkVerificationRequest,
  ) {
    let params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('netId', requestParams.netId)
      .set('netType', requestParams.netType)
      .set('provId', requestParams.provId)
      .set('zip', requestParams.zip);

    if (requestParams.asofDate) {
      params = params.append('asofDate', requestParams.asofDate);
    }
    if (requestParams.provType) {
      params = params.append('provType', requestParams.provType);
    }

    return this.http
      .get(`${this.providerApi}/network-verification`, { params })
      .pipe(
        map(checkValidResponse),
        map((data): string => data.networkStatus),
        catchError(logError),
      );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getFnsNetworkVerification(requestParams: FnsNetworkVerificationRequest) {
    let params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('clientProviderId', requestParams.clientProviderId)
      .set('tin', requestParams.tin)
      .set('networkDetails', requestParams.networkDetails)
      .set('DateOfService', requestParams.dateOfService)
      .set('mhsrIndicator', requestParams.coverageType === 'MHSR' ? 'Y' : 'N');

    if (
      requestParams.networkCategoryCode.toLowerCase() === 'ecn' ||
      requestParams.coverageType === 'MHSR'
    ) {
      params = params.append('healthPlanId', requestParams.networkId);
    } else {
      params = params.append('NetworkId', requestParams.networkId);
    }

    if (requestParams.isPathwell) {
      params = params.append('pathwellIndicator', 'Y');
    }
    if (requestParams.hasIFP) {
      params = params.append('ifpIndicator', 'Y');
    }

    return this.http
      .get(`${this.providerApi}/fns-network-verification`, { params })
      .pipe(
        map(checkValidResponse),
        map((data): string =>
          requestParams.isPathwell
            ? data.networkStatus === 'Y' && data.pathwellNetworkStatus === 'Y'
              ? 'pathwellInclude'
              : data.networkStatus === 'Y' && data.pathwellNetworkStatus === 'N'
              ? 'pathwellExclude'
              : 'pathwellOON'
            : data.networkStatus,
        ),
        catchError(logError),
      );
  }

  getPathwellProviderNetworkVerification(
    requestParams: PathwellNetworkVerificationRequest,
  ) {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('healthPlanId', requestParams.healthPlanId)
      .set('clientProviderId', requestParams.clientProviderId)
      .set('tin', requestParams.tin)
      .set('networkDetails', requestParams.networkDetails)
      .set('DateOfService', requestParams.dateOfService);

    return this.http
      .get(`${this.providerApi}/pathwell-network-verification`, { params })
      .pipe(
        map(checkValidResponse),
        map((data): string =>
          data.networkStatus === 'Y' && data.pathwellNetworkStatus === 'Y'
            ? 'pathwellInclude'
            : data.networkStatus === 'Y' && data.pathwellNetworkStatus === 'N'
            ? 'pathwellExclude'
            : 'pathwellOON',
        ),
        catchError(logError),
      );
  }

  getIFPProviderNetworkVerification(
    requestParams: PathwellNetworkVerificationRequest,
  ) {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .set('healthPlanId', requestParams.healthPlanId)
      .set('clientProviderId', requestParams.clientProviderId)
      .set('tin', requestParams.tin)
      .set('networkDetails', requestParams.networkDetails)
      .set('DateOfService', requestParams.dateOfService);

    return this.http
      .get(`${this.providerApi}/ifp-network-verification`, { params })
      .pipe(
        map(checkValidResponse),
        map((data): string => data.networkStatus),
        catchError(logError),
      );
  }

  getProvidersForDDByTin(tin: string) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .get(`${this.providerApi}/providers/tin/${tin}/directdeposit`, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => res.providers),
        catchError(logError),
      );
  }

  getUserRedirectData(collectionId: string) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .get(`${this.providerApi}/collection/${collectionId}`, { params })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  updateUserRedirectData(payload: { type: string; data: object }) {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.CONSUMER_CODE)
      .append('type', payload.type);

    return this.http
      .post(`${this.providerApi}/collection`, payload.data, { params })
      .pipe(map(checkValidResponse), catchError(logError));
  }
}
